import { useEffect, useRef } from 'react';
import { useGeneration } from '../hooks/useGeneration';

export class Observable<T> {
  constructor(private value: T) {}
  private listeners = new Set<Listener<T>>();

  listen = (listener: Listener<T>) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  setValue = (nextValue: T) => {
    this.value = nextValue;
    this.listeners.forEach(listener => listener(this.value));
  };

  getValue = () => this.value;
}

type Listener<T> = (value: T) => void;

export const useObservable = <T>(observable: Observable<T>) => {
  const value = observable.getValue();
  const lastRendered = useRef(value);
  const [, render] = useGeneration();

  useEffect(
    () =>
      observable.listen(nextValue => {
        /** A previous listener higher in the tree may have already caused this component to
         * re-render. We bail out of cascading re-renders by checking first. */
        if (lastRendered.current !== nextValue) {
          render();
        }
      }),
    [observable, render],
  );

  useEffect(() => {
    lastRendered.current = value;
  });

  return value;
};
